import moment from 'moment';
import i18n from 'helpers/i18n';
import messages from './messages';
import { Link } from 'react-router-dom';
import { roleToText } from 'helpers/role';
import { toTitleCase } from 'helpers/text';
import { Table } from 'components/Organization/Table';
import { StatusBadge } from 'components/Organization/StatusBadge';
import {
  MembershipTrackerRecord,
  useGetUserMembershipTrackers
} from 'actions/membershipTrackers';
import { dcRoles } from 'pages/Organization/ViewSubscriptionUsers/utils';
import { Combobox } from '@teachstonellc/component-library';
import { EditableField } from 'components/Organization/EditableField';
import { updateSubscriptionUserRole } from 'actions/subscriptions';
import { toastError } from 'components/Organization/Toast';

interface SubscriptionsTableProps {
  userId: number;
}

export function SubscriptionsTable({ userId }: SubscriptionsTableProps) {
  const { data, isLoading, refetch } = useGetUserMembershipTrackers(userId);
  const trackers = data?.membership_trackers ?? [];

  function getSubscriptionLink(subscriptionId: number) {
    return `/organization/subscriptions/${subscriptionId}/users`;
  }

  function formatDate(date?: string) {
    if (!date) {
      return '--';
    }

    return moment(date).format('L');
  }

  const isDCSubscription = (tracker: MembershipTrackerRecord) => {
    return tracker.product_name === 'data_collection';
  };

  const initialRole = (tracker: MembershipTrackerRecord) => {
    return {
      label: roleToText(tracker.role),
      value: tracker.role
    };
  };

  const rows = trackers.map(tracker => ({
    product_name: (
      <Link
        className="font-bold text-black underline hover:underline hover:text-purple-600"
        to={getSubscriptionLink(tracker.subscription_id)}
      >
        {toTitleCase(tracker.product_name)}
      </Link>
    ),
    role: isDCSubscription(tracker) ? (
      <EditableField
        label={i18n.ft(messages.role)}
        value={roleToText(tracker.role)}
        initialValue={tracker.role}
        onSave={value => {
          updateSubscriptionUserRole(
            tracker.subscription_id,
            userId,
            value.value
          )
            .then(() => {
              refetch();
            })
            .catch(() => {
              toastError({
                message: i18n.ft(messages.updateRoleError)
              });
            });
        }}
        border={false}
        showLabel={false}
        small
        control={({ value, setValue }) => (
          <Combobox
            label={i18n.ft(messages.role)}
            showLabel={false}
            options={dcRoles}
            placeholder={i18n.ft(messages.rolePlaceholder)}
            selectedOption={
              typeof value === 'object' ? value : initialRole(tracker)
            }
            onChange={newValue => setValue(newValue)}
            showSearchIcon={false}
            showDropdownIcon
            small
          />
        )}
      />
    ) : (
      roleToText(tracker.role)
    ),
    status: <StatusBadge status={tracker.status} />,
    enrolled_on: formatDate(tracker.enrolled_on),
    invited_on: formatDate(tracker.invited_on)
  }));

  return (
    <div className="font-sans bg-white p-6 rounded-2xl mt-6">
      <div className="font-bold text-xl md:text-2xl">
        {i18n.ft(messages.header)}
      </div>

      <div className="mt-10">
        <Table
          isLoading={isLoading}
          headers={[
            {
              accessor: 'product_name',
              content: i18n.ft(messages.productName)
            },
            { accessor: 'role', content: i18n.ft(messages.role) },
            { accessor: 'status', content: i18n.ft(messages.status) },
            { accessor: 'invited_on', content: i18n.ft(messages.invitedOn) },
            { accessor: 'enrolled_on', content: i18n.ft(messages.enrolledOn) }
          ]}
          data={rows}
        />
      </div>
    </div>
  );
}
